import { formatHexColor } from './editorUnits';
import type { ColorArea } from '../Reducers/Configurator/types';

export type SvgPaintProperty = 'fill' | 'stroke';

function mergeStyleDeclaration(style: string, property: SvgPaintProperty, value: string): string {
  const parts = style
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part && !new RegExp(`^${property}(?:-|$)`, 'i').test(part.split(':')[0]?.trim() || ''));
  parts.push(`${property}: ${value}`);
  return parts.join('; ');
}

/** Override class-based CSS for one paint property while keeping other class rules intact. */
export function setElementPaintColor(element: Element, paint: SvgPaintProperty, color: string): void {
  const hex = formatHexColor(color);
  const stylable = element as Element & { style?: CSSStyleDeclaration };
  if (stylable.style?.setProperty) {
    stylable.style.setProperty(paint, hex);
  } else {
    const nextStyle = mergeStyleDeclaration(element.getAttribute('style') ?? '', paint, hex);
    element.setAttribute('style', nextStyle);
  }
  element.setAttribute(paint, hex);
}

function applyAreaColorToElement(element: Element, hex: string, paint: SvgPaintProperty): void {
  setElementPaintColor(element, paint, hex);
}

function elementHasStrokePaint(element: Element): boolean {
  const stroke = element.getAttribute('stroke');
  if (stroke && stroke !== 'none' && stroke !== 'transparent') return true;
  const style = element.getAttribute('style') ?? '';
  return /stroke\s*:\s*(?!none|transparent)\S/i.test(style);
}

/** Apply fill-area color honoring data-color-paint and dual stroke areas. */
function applyFillAreaColor(node: Element, hex: string): void {
  const paint = node.getAttribute('data-color-paint');
  if (paint === 'both') {
    applyAreaColorToElement(node, hex, 'fill');
    applyAreaColorToElement(node, hex, 'stroke');
    return;
  }
  if (paint === 'stroke') {
    applyAreaColorToElement(node, hex, 'stroke');
    return;
  }

  applyAreaColorToElement(node, hex, 'fill');
  if (!node.hasAttribute('data-stroke-color-area') && elementHasStrokePaint(node)) {
    applyAreaColorToElement(node, hex, 'stroke');
  }
}

function applyColorAreasToSvgViaDom(svgContent: string, colorAreas: ColorArea[]): string {
  if (!svgContent?.trim() || colorAreas.length === 0) return svgContent;

  try {
    const doc = new DOMParser().parseFromString(svgContent, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return svgContent;

    colorAreas.forEach((area, index) => {
      const hex = formatHexColor(area.color);

      doc.querySelectorAll(`[data-color-area="${index}"]`).forEach((node) => {
        applyFillAreaColor(node, hex);
      });

      doc.querySelectorAll(`[data-stroke-color-area="${index}"]`).forEach((node) => {
        applyAreaColorToElement(node, hex, 'stroke');
      });
    });

    return new XMLSerializer().serializeToString(doc.documentElement);
  } catch {
    return svgContent;
  }
}

export function replaceSvgColorAreas(svgContent: string, colorAreas: ColorArea[]): string {
  return applyColorAreasToSvgViaDom(svgContent, colorAreas);
}
